import React, { useState, useRef, useEffect } from 'react';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader';
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import './EnhancedBIMViewer.css';

/**
 * Enhanced 3D BIM Viewer with Paint Support
 * Supports: GLB, GLTF, OBJ, STL, and JSON (converted RVT)
 */
const EnhancedBIMViewer = ({
  modelPath,
  modelName,
  onPaintData,
  paintMode = false,
  paintColor = '#FF0000',
}) => {
  const containerRef = useRef(null);
  const sceneRef = useRef(null);
  const rendererRef = useRef(null);
  const cameraRef = useRef(null);
  const controlsRef = useRef(null);
  const raycasterRef = useRef(new THREE.Raycaster());
  const mouseRef = useRef(new THREE.Vector2());
  const paintDataRef = useRef({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modelStats, setModelStats] = useState({
    vertices: 0,
    triangles: 0,
    materials: 0,
  });

  useEffect(() => {
    if (!containerRef.current || !modelPath) return;

    initializeViewer();
    return cleanup;
  }, [modelPath]);

  useEffect(() => {
    if (!paintMode) return;

    const handleMouseMove = (e) => {
      const rect = rendererRef.current.domElement.getBoundingClientRect();
      mouseRef.current.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      mouseRef.current.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
    };

    const handleClick = () => paintAtMouse();

    rendererRef.current.domElement.addEventListener('mousemove', handleMouseMove);
    rendererRef.current.domElement.addEventListener('click', handleClick);

    return () => {
      rendererRef.current?.domElement.removeEventListener('mousemove', handleMouseMove);
      rendererRef.current?.domElement.removeEventListener('click', handleClick);
    };
  }, [paintMode, paintColor]);

  const initializeViewer = async () => {
    try {
      setLoading(true);
      const width = containerRef.current.clientWidth;
      const height = containerRef.current.clientHeight;

      // Scene setup
      const scene = new THREE.Scene();
      scene.background = new THREE.Color(0x1a1a1a);
      sceneRef.current = scene;

      // Camera setup
      const camera = new THREE.PerspectiveCamera(75, width / height, 0.1, 10000);
      camera.position.set(0, 50, 100);
      cameraRef.current = camera;

      // Renderer setup
      const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
      renderer.setSize(width, height);
      renderer.shadowMap.enabled = true;
      renderer.shadowMap.type = THREE.PCFSoftShadowMap;
      containerRef.current.appendChild(renderer.domElement);
      rendererRef.current = renderer;

      // Lighting
      const ambientLight = new THREE.AmbientLight(0xffffff, 0.7);
      scene.add(ambientLight);

      const directionalLight = new THREE.DirectionalLight(0xffffff, 0.9);
      directionalLight.position.set(50, 100, 50);
      directionalLight.castShadow = true;
      directionalLight.shadow.mapSize.width = 2048;
      directionalLight.shadow.mapSize.height = 2048;
      directionalLight.shadow.camera.far = 10000;
      scene.add(directionalLight);

      // Grid
      const gridHelper = new THREE.GridHelper(200, 20);
      scene.add(gridHelper);

      // Controls
      const controls = new OrbitControls(camera, renderer.domElement);
      controls.autoRotate = false;
      controls.enableDamping = true;
      controls.dampingFactor = 0.05;
      controlsRef.current = controls;

      // Load model
      await loadModel(modelPath);

      // Animation loop
      const animate = () => {
        requestAnimationFrame(animate);
        controls.update();
        renderer.render(scene, camera);
      };
      animate();

      // Handle resize
      const handleResize = () => {
        const newWidth = containerRef.current.clientWidth;
        const newHeight = containerRef.current.clientHeight;
        camera.aspect = newWidth / newHeight;
        camera.updateProjectionMatrix();
        renderer.setSize(newWidth, newHeight);
      };
      window.addEventListener('resize', handleResize);

      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const loadModel = async (path) => {
    const ext = path.split('.').pop().toLowerCase();

    try {
      if (ext === 'glb' || ext === 'gltf') {
        await loadGLTF(path);
      } else if (ext === 'obj') {
        await loadOBJ(path);
      } else if (ext === 'stl') {
        await loadSTL(path);
      } else if (ext === 'json') {
        await loadJSON(path);
      } else if (ext === 'ifc') {
        await loadIFC(path);
      } else {
        throw new Error(`Unsupported format: ${ext}`);
      }
    } catch (err) {
      throw new Error(`Failed to load model: ${err.message}`);
    }
  };

  const loadGLTF = (path) => {
    return new Promise((resolve, reject) => {
      const loader = new GLTFLoader();
      loader.load(
        path,
        (gltf) => {
          const model = gltf.scene;
          sceneRef.current.add(model);
          updateModelStats(model);
          resolve();
        },
        undefined,
        reject
      );
    });
  };

  const loadOBJ = (path) => {
    return new Promise((resolve, reject) => {
      const loader = new OBJLoader();
      loader.load(
        path,
        (object) => {
          sceneRef.current.add(object);
          updateModelStats(object);
          resolve();
        },
        undefined,
        reject
      );
    });
  };

  const loadSTL = (path) => {
    return new Promise((resolve, reject) => {
      const loader = new STLLoader();
      loader.load(
        path,
        (geometry) => {
          geometry.center();
          const material = new THREE.MeshPhongMaterial({ color: 0x00aaff });
          const mesh = new THREE.Mesh(geometry, material);
          sceneRef.current.add(mesh);
          updateModelStats(mesh);
          resolve();
        },
        undefined,
        reject
      );
    });
  };

  const loadJSON = (path) => {
    return new Promise((resolve, reject) => {
      fetch(path)
        .then((res) => res.json())
        .then((data) => {
          // Load Three.js JSON format
          if (data.object) {
            loadThreeJSONObject(data.object);
          }
          resolve();
        })
        .catch(reject);
    });
  };

  const loadIFC = async (path) => {
    // Dynamic import to avoid webpack issues with web-ifc-three
    const { IFCLoader } = await import('web-ifc-three');
    
    return new Promise((resolve, reject) => {
      const loader = new IFCLoader();
      loader.load(
        path,
        (ifcModel) => {
          // Apply material to all meshes in the IFC model
          ifcModel.traverse((child) => {
            if (child.isMesh) {
              child.material = new THREE.MeshStandardMaterial({
                color: 0x9e9e9e,
                metalness: 0.1,
                roughness: 0.8,
              });
            }
          });
          sceneRef.current.add(ifcModel);
          updateModelStats(ifcModel);
          resolve();
        },
        undefined,
        reject
      );
    });
  };

  const loadThreeJSONObject = (objData) => {
    if (objData.type === 'Mesh') {
      const geometry = new THREE.PlaneGeometry();
      const material = new THREE.MeshStandardMaterial({
        color: objData.material?.color || 0x888888,
      });
      const mesh = new THREE.Mesh(geometry, material);
      sceneRef.current.add(mesh);
    } else if (objData.children) {
      objData.children.forEach((child) => loadThreeJSONObject(child));
    }
  };

  const updateModelStats = (model) => {
    let vertices = 0;
    let triangles = 0;
    const materials = new Set();

    model.traverse((child) => {
      if (child.geometry) {
        if (child.geometry.attributes.position) {
          vertices += child.geometry.attributes.position.count;
        }
        if (child.geometry.index) {
          triangles += child.geometry.index.count / 3;
        }
      }
      if (child.material) {
        materials.add(child.material.uuid);
      }
    });

    setModelStats({
      vertices: Math.floor(vertices),
      triangles: Math.floor(triangles),
      materials: materials.size,
    });
  };

  const paintAtMouse = () => {
    if (!paintMode) return;

    raycasterRef.current.setFromCamera(mouseRef.current, cameraRef.current);
    const intersects = raycasterRef.current.intersectObjects(sceneRef.current.children, true);

    if (intersects.length > 0) {
      const intersection = intersects[0];
      const object = intersection.object;

      if (object.material) {
        // Store original color
        if (!paintDataRef.current[object.uuid]) {
          paintDataRef.current[object.uuid] = {
            originalColor: object.material.color.getHex(),
            paintHistory: [],
          };
        }

        // Apply paint
        object.material.color.setStyle(paintColor);
        paintDataRef.current[object.uuid].paintHistory.push({
          color: paintColor,
          timestamp: Date.now(),
          point: intersection.point.toArray(),
        });

        // Notify parent
        if (onPaintData) {
          onPaintData(paintDataRef.current);
        }
      }
    }
  };

  const cleanup = () => {
    if (rendererRef.current && containerRef.current) {
      containerRef.current.removeChild(rendererRef.current.domElement);
      rendererRef.current.dispose();
    }
  };

  return (
    <div className="bim-viewer">
      <div className="bim-viewer-header">
        <h3>{modelName}</h3>
        <div className="model-stats">
          <span>Vertices: {modelStats.vertices.toLocaleString()}</span>
          <span>Triangles: {modelStats.triangles.toLocaleString()}</span>
          <span>Materials: {modelStats.materials}</span>
        </div>
      </div>

      <div className="bim-viewer-container" ref={containerRef}>
        {loading && (
          <div className="loading-overlay">
            <div className="spinner"></div>
            <p>Loading 3D Model...</p>
          </div>
        )}
        {error && (
          <div className="error-overlay">
            <p>{error}</p>
          </div>
        )}
        {paintMode && (
          <div className="paint-mode-indicator">
            <span className="paint-brush">🎨</span>
            Paint Mode Active - Click to paint
          </div>
        )}
      </div>
    </div>
  );
};

export default EnhancedBIMViewer;
